import ListHeader from "@/common/components/ListHeader";
import TextField from "@/common/components/TextField";
import Table from "@/common/components/Table";
import ListFilters from "@/common/components/ListFilters";
import { useFilterParams } from "@/common/hooks/useFilterParams";
import useDebounce from "@/common/hooks/useDebounce";
import { ErrorMessageWrapper } from "@/common/components/ErrorMessageWrapper";
import { missionsColumns } from "../../constants/columns.constant";
import { useMissionsList } from "../../hooks/useMissions";
import { Link } from "react-router-dom";
import Button from "@/common/components/Button";
import { Search, Goal } from "lucide-react";
import { Option } from "@/common/types/option";
import { Mission } from "../../types/mission.type";
import useUserPermissions from "@/common/hooks/useUserPermissions";
import { MissionsTableTransformer } from "../../services/missionsTableTransformer";

const statusFilterOptions: Option[] = [
  { label: "Todos", value: "" },
  { label: "Inativa", value: "inactive" },
  { label: "Ativa", value: "active" },
];

const MissionsList = () => {
  const { filterParams, setFilterParams } = useFilterParams({});

  const [search, setSearch] = useDebounce(
    (filterParams.search as string) || "",
  );
  const { hasUserPermission } = useUserPermissions();
  const canCreateMission = hasUserPermission(
    "Establishment::Tmais::Missions::Create",
  );
  const canFilterMissions = hasUserPermission(
    "Establishment::Tmais::Missions::Filter",
  );
  const canShowMission = hasUserPermission(
    "Establishment::Tmais::Missions::Show",
  );

  const rawStatus = (filterParams.status as string | undefined) || undefined;
  const statusValue = rawStatus || "";

  const { data, isLoading, error, refetch } = useMissionsList({
    filters: {
      search,
      status: statusValue as Mission["status"] | "",
      page: Number(filterParams.page) || 1,
    },
  });

  const missions = data?.data || [];
  const meta = data?.meta || { total: missions.length, total_pages: 1 };
  const currentPage = Number(filterParams.page) || 1;

  const rows = MissionsTableTransformer.mapToRows(missions, {
    canShowMission,
  });

  const createMissionButton = (
    <Button
      className="shadow-[4px_4px_8px_4px_rgba(0,_0,_0,_0.2)]"
      operationKlassToCheckPermission="Establishment::Tmais::Missions::Create"
    >
      Cadastrar missão
    </Button>
  );

  return (
    <div className="flex flex-col gap-6">
      <ListHeader
        Icon={<Goal className="w-10 h-10" strokeWidth={1.5} />}
        title="Missões"
        actions={
          canCreateMission ? (
            <Link to="/tmais-missions/new">{createMissionButton}</Link>
          ) : (
            createMissionButton
          )
        }
      />

      <ErrorMessageWrapper
        className="h-[calc(100vh-128px)]"
        statusCode={(error as any)?.status || 200}
        refetchFunction={refetch}
      >
        <div className="flex flex-col gap-6 h-[calc(100vh-128px)]">
          <TextField
            LeftIcon={<Search />}
            placeholder="Pesquisar"
            className="rounded-sm shadow-[4px_0px_16px_0px_rgba(0,_0,_0,_0.5)]"
            value={(filterParams.search as string) || ""}
            disabled={!canFilterMissions}
            onChange={(event) => {
              const value = (event.target as HTMLInputElement).value;
              setSearch(value);
              setFilterParams((prev: any) => ({
                ...prev,
                search: value,
                page: 1,
              }));
            }}
          />

          <ListFilters
            filters={[
              {
                className: "w-[200px]",
                placeholder: "Status",
                options: statusFilterOptions,
                disabled: !canFilterMissions,
                operationKlassToCheckPermission:
                  "Establishment::Tmais::Missions::Filter",
                value:
                  statusValue === ""
                    ? null
                    : statusFilterOptions.find(
                        (option) => option.value === statusValue,
                      ) || null,
                onChange: (option) =>
                  setFilterParams((prev: any) => ({
                    ...prev,
                    status: option ? (option as Option).value : "",
                    page: 1,
                  })),
              },
            ]}
          />

          <Table
            columns={missionsColumns}
            rows={rows}
            isLoadingData={isLoading}
            page={currentPage}
            totalPages={meta.total_pages || 1}
            totalCount={meta.total || missions.length}
            hasPagination
            setPage={(page) =>
              setFilterParams((prev: any) => ({ ...prev, page }))
            }
            rowEmptyValue="--"
          />
        </div>
      </ErrorMessageWrapper>
    </div>
  );
};

export default MissionsList;




